export const FaseObj = [
    {
        desc: "Consuntivo",
        id: 2,
        meta: {}
    },
    {
        desc: "Periodico",
        id: 1,
        meta: {}
    },
    {
        desc: "Preventivo",
        id: 3,
        meta: {}
    }
];

export type FaseDocumento =
  | 'Periodico'
  | 'Preventivo'
  | 'Consuntivo';

export interface DocumentoMeta {
  fase: FaseDocumento;
}

export interface Documento {
  desc: string;
  id: number;
  meta: DocumentoMeta;
}

/** Opzione del multiselect 'Tipo Ente', nella forma emessa dal widget quando l'utente seleziona. */
export interface TipoEnteOption {
  readonly id: number;
  readonly text: string;
  readonly disabled: boolean;
}

/**
 * Forme che il form control 'tipoEnte' può assumere a runtime:
 * - `TipoEnteOption[]` quando il valore arriva dal multiselect (selezione interattiva dell'utente);
 * - `number[]` quando il valore viene ripristinato dai filtri salvati nello store, che conservano
 *   i soli id (`listaTipiEnte`). Il widget mostra comunque l'etichetta corretta, perché risolve il
 *   testo dal proprio dataset a partire dall'id.
 */
export type TipoEnteControlValue = readonly (TipoEnteOption | number)[] | null | undefined;

/**
 * Estrae gli id dal valore del form control 'tipoEnte' convertendo entrambe le forme che può
 * assumere (vedi `TipoEnteControlValue`).
 * @important senza questa conversione il valore ripristinato (array di id) produce
 * `listaTipiEnte` vuoto, perché gli id grezzi non hanno la proprietà `id`: il filtro Tipo Ente
 * verrebbe silenziosamente perso nella prima ricerca successiva a un ripristino.
 */
export function extractTipoEnteIds(value: TipoEnteControlValue): number[] {
  return (value ?? [])
    .map((item): number | undefined => (typeof item === 'number' ? item : item?.id))
    .filter((id): id is number => id != null);
}

/** Parametri di ricerca documenti accettati dal backend. */
export interface RicercaDocumentiParams {
  text?: string;
  sequEsercizio?: number;
  sequFase?: number;
  listaTipiEnte?: number[];
  sequRegione?: number;
  sequProvincia?: number;
  sequEnte?: string;
  sequAdempimento?: number;
  sequTipoDocumento?: number;
  sequStato?: number;
  sequFonte?: number;
  dataInvioMin?: string;
  dataInvioMax?: string;
  richiestaInvio?: boolean;
}

export function buildRicercaDocumentiQueryString(params: RicercaDocumentiParams): string {
  const parts: string[] = [];

  const append = (key: string, value: string | number | boolean): void => {
    parts.push(`${key}=${encodeURIComponent(String(value))}`);
  };

  if (params.text?.trim()) {
    append('text', params.text.trim());
  }
  if (params.sequEsercizio != null) {
    append('sequEsercizio', params.sequEsercizio);
  }
  if (params.sequFase != null) {
    append('sequFase', params.sequFase);
  }
  if (params.listaTipiEnte?.length) {
    append('listaTipiEnte', params.listaTipiEnte.join(','));
  }
  if (params.sequRegione != null) {
    append('sequRegione', params.sequRegione);
  }
  if (params.sequProvincia != null) {
    append('sequProvincia', params.sequProvincia);
  }
  if (params.sequEnte) {
    append('sequEnte', params.sequEnte);
  }
  if (params.sequAdempimento != null) {
    append('sequAdempimento', params.sequAdempimento);
  }
  if (params.sequTipoDocumento != null) {
    append('sequTipoDocumento', params.sequTipoDocumento);
  }
  if (params.sequStato != null) {
    append('sequStato', params.sequStato);
  }
  if (params.sequFonte != null) {
    append('sequFonte', params.sequFonte);
  }
  if (params.dataInvioMin) {
    append('dataInvioMin', params.dataInvioMin);
  }
  if (params.dataInvioMax) {
    append('dataInvioMax', params.dataInvioMax);
  }
  if (params.richiestaInvio != null) {
    append('richiestaInvio', params.richiestaInvio);
  }

  return parts.join('&');
}

/** Estrae yyyy-MM-dd dal valore del datepicker. */
function toDateOnlyString(value: string | Date): string {
  if (value instanceof Date) {
    const yyyy = value.getFullYear();
    const mm = String(value.getMonth() + 1).padStart(2, '0');
    const dd = String(value.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  return value.split('T')[0];
}

/** Normalizza la data del datepicker nel formato yyyy-MM-dd atteso dal BE. */
export function toRicercaDateParam(value: string | Date): string {
  return toDateOnlyString(value);
}